/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Globals Store - State Used Across the Entire Application
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { AlertSnackbar } from '@/types/typesGlobal'

export const useGlobalsStore = defineStore('globalsStore', () => {
  // //////////////////////////////////////////////////////////////////
  // 💬 System Globals
  // //////////////////////////////////////////////////////////////////
  const applicationName = ref('Pulsar')
  const applicationOfficialName = ref('PulsarAi')
  const applicationDomain = ref(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://app.pulsarai.ai')
  const productName = ref('Snowflake')

  // 💬 Timezone Related State
  // ///////////////////////////////////////////////////////////////////
  //
  // `userTimezone` is the EFFECTIVE timezone used for date / time
  // rendering across the app. Resolution order, highest priority first:
  //   1. `users/{uid}.preferences.timezone` (set on the settings page)
  //   2. Browser-detected via `Intl.DateTimeFormat().resolvedOptions().timeZone`
  //   3. `'UTC'` as a last resort (covers SSR + Intl-less sandboxes)
  //
  // The user profile sync below populates `userTimezone` from the
  // doc when it lands; until then we render in the browser-detected
  // zone so initial paint isn't wrong. All TIMESTAMPS in the
  // database stay UTC — this only affects DISPLAY.
  const userTimezone = ref(_detectBrowserTimezone())

  // Internal detector — same logic as `useUserTimezone.detectBrowserTimezone`,
  // duplicated here so the store doesn't pull the composable in
  // (which would create a setup-store cycle through the composable's
  // own `useGlobalsStore` call).
  function _detectBrowserTimezone(): string {
    if (typeof Intl === 'undefined') return 'UTC'
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      return (tz && typeof tz === 'string') ? tz : 'UTC'
    }
    catch {
      return 'UTC'
    }
  }

  // const applicationDomainLocalEmulation = ref('http://localhost:3000')
  const docStore = useDocStore()
  const { getDoc } = docStore

  // //////////////////////////////////////////////////////////////////
  // 💬 Global Layouts
  // //////////////////////////////////////////////////////////////////
  const railCollapsedState = ref(true)

  // //////////////////////////////////////////////////////////////////
  // 💬 Account Profile Information
  // //////////////////////////////////////////////////////////////////

  // Note* Account and User Docs are Synced in the docStore.docs object
  const accountProfileDocSync = ref({} as any)
  const accountId = ref(null as string | null)
  const userId = ref(null as string | null)
  const accountNamespace = ref(null as string | null) // Default Snowflake Namespace

  const syncAccountProfile = (account: string) => {
    accountId.value = account

    getDoc('accountProfile', 'accounts', account, true, accountProfileDocSync)
  }

  const accountProfile = computed(() => {
    // 💬 Get Snowflake System Namespace
    // /////////////////////////////////
    const systemDatabase = accountProfileDocSync.value?.snowflakeSettings?.systemOwnership?.database || ''
    const systemSchema = accountProfileDocSync.value?.snowflakeSettings?.systemOwnership?.schema || ''

    accountNamespace.value = systemDatabase ? `${systemDatabase}.${systemSchema}` : null

    return accountProfileDocSync.value
  })

  // 💬 Native App engine feature flag
  // /////////////////////////////////
  //
  // When `accounts/{id}.settings.nativeEngine === true`, the workflow
  // stores persist to the SnowFlow Snowflake Native App's SQL API
  // instead of Firestore. `nativeAppName` is the per-account app
  // database name (the name the customer installed the app under);
  // validated against a strict identifier pattern before any SQL
  // interpolation — invalid values fall back to the default.
  const nativeEngine = computed<boolean>(() => {
    return accountProfileDocSync.value?.settings?.nativeEngine === true
  })

  const nativeAppName = computed<string>(() => {
    const raw = accountProfileDocSync.value?.settings?.nativeAppName
    if (typeof raw === 'string' && /^[A-Za-z0-9_]+$/.test(raw)) return raw
    return 'SNOWFLOW_APP'
  })

  // 💬 User Profile Information
  // /////////////////////////////////
  const userProfileDocSync = ref({} as any)

  const syncUserProfile = (account: string, uid: string) => {
    userId.value = uid
    getDoc('userProfile', 'users', uid, true, userProfileDocSync)
  }

  // Keep `userTimezone` in sync with the user's saved preference. The
  // doc lands asynchronously after auth, so on first paint we render
  // in the browser-detected zone (set above); when the doc shows up
  // with `preferences.timezone`, this watcher swaps to that.
  watch(
    () => userProfileDocSync.value?.preferences?.timezone,
    (saved) => {
      if (saved && typeof saved === 'string' && saved.length > 0) {
        userTimezone.value = saved
      }
      else {
        // User hasn't saved a preference (or just cleared it). Fall
        // back to browser detection — preserves the autodetect path
        // when the user clicks "Use my browser's timezone".
        userTimezone.value = _detectBrowserTimezone()
      }
    },
    { immediate: true },
  )

  const userProfile = computed(() => {
    const { firstName, lastName, snowflakeConnections, defaults, accounts } = userProfileDocSync.value
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : ''
    const initials = firstName && lastName ? `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}` : ''

    // Get Active Snowflake Connection from Defaults if Available
    let activeSnowflakeConnection = snowflakeConnections && defaults?.snowflakeConnection ? snowflakeConnections[defaults.snowflakeConnection] : null

    // If Not Using a Snowflake Connection, Set Active Snowflake Connection to Null
    if (activeSnowflakeConnection && activeSnowflakeConnection.connectionType === 'none') activeSnowflakeConnection = null

    // Derive the user's role for the CURRENT account.
    //
    // Source of truth is `users/{uid}.accounts.{accountId}.role` (set by
    // app_register_account at signup, updated by future "manage members"
    // flows). The Firebase JWT also carries a `role` custom claim, but
    // that's only refreshed on token refresh — the doc is always
    // current, so we read from the doc.
    //
    // `roleLabel` is the title-cased version for display ("Owner" /
    // "Admin"). Components that need to gate behavior (e.g. "is this
    // user at least an editor") should read the lowercase `role`
    // instead and check it against a known set.
    const rawRole = (accounts && accountId.value && accounts[accountId.value]?.role) || ''
    const role = String(rawRole || '').toLowerCase()
    const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : ''

    return { ...userProfileDocSync.value, fullName, initials, activeSnowflakeConnection, role, roleLabel }
  })

  // //////////////////////////////////////////////////////////////////
  // 💬 Live Snowflake Session
  // //////////////////////////////////////////////////////////////////
  //
  // Populated by the StatusIndicator chip's session probe. Any
  // component that needs to know "what role / warehouse / database
  // is the user currently operating under in Snowflake?" reads this
  // (e.g. the header shows the role under the user's name; future
  // dashboards may key data scope off this).
  //
  // Null when the probe hasn't completed or the user has no live
  // Snowflake session (e.g. signed in to Pulsar via email/password
  // before connecting their Snowflake account). Components should
  // treat null as "no live context" and fall back to a sensible
  // placeholder rather than blanking entirely.
  //
  // The probe re-runs every 60s, so this is reactive — flipping
  // roles via `USE ROLE ...` in another tool would propagate here on
  // the next refresh cycle.
  interface SnowflakeSession {
    user: string
    role: string
    warehouse: string
    database: string
    schema: string
    region: string
    account: string
    version: string
    sessionId: string
  }
  const snowflakeSession = ref<SnowflakeSession | null>(null)
  function setSnowflakeSession(s: SnowflakeSession | null) {
    snowflakeSession.value = s
  }

  // 💬 List of roles available to the user (CURRENT_AVAILABLE_ROLES())
  // populated by the status probe. The role switcher in the header
  // reads from this; null/empty means we haven't successfully probed
  // yet.
  const availableSnowflakeRoles = ref<string[]>([])
  function setAvailableSnowflakeRoles(roles: string[]) {
    availableSnowflakeRoles.value = roles
  }

  // 💬 The user's chosen active Snowflake role.
  //
  // Sourced from `users/{uid}.snowflakeConnections.{connId}.activeRole`
  // — set by the role switcher, persisted across sessions, applied
  // on every Snowflake query (browser path: passed as `role` in the
  // request body; backend path: passed to the connector). When unset,
  // Snowflake uses the user's DEFAULT_ROLE on connect.
  //
  // The setter writes to Firestore via useSetDoc so the change syncs
  // across tabs / devices and the StatusIndicator picks it up via
  // the userProfileDocSync subscription.
  const activeSnowflakeRole = computed<string>(() => {
    return userProfile.value?.activeSnowflakeConnection?.activeRole || ''
  })

  async function setActiveSnowflakeRole(role: string) {
    if (!userId.value) return
    const connId = userProfile.value?.defaults?.snowflakeConnection || '1'
    // Firestore expects `null` to clear, not undefined. Normalize an
    // empty string → null so the user can return to "default role"
    // by picking nothing.
    const value = role || null
    await useSetDoc('users', userId.value, {
      snowflakeConnections: {
        [connId]: {
          activeRole: value,
          modifiedAt: new Date(),
        },
      },
    })
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Application Theme
  // //////////////////////////////////////////////////////////////////
  // "theme" has a cookie for remembering the user's theme preference.
  // Default is dark — Pulsar's UI is designed for dark mode (the
  // default Tailwind color variants and most marketing screenshots
  // assume it). The cookie overrides this once the user toggles.
  const theme = ref('dark' as 'light' | 'dark' | 'system') // app.vue will check for an active cookie
  const themeCookie = useCookie('theme')

  function toggleTheme() {
    theme.value = theme.value === 'dark' ? 'light' : 'dark'
    themeCookie.value = theme.value
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Dashboard Filters
  // //////////////////////////////////////////////////////////////////


  // //////////////////////////////////////////////////////////////////
  // 💬 Throw Global Snackbar Alert
  // //////////////////////////////////////////////////////////////////
  const alertSnackbarState = ref<AlertSnackbar>({ visible: false, color: 'info', message: undefined, timeout: 3000 })

  const alertSnackbar = (message: string, color: 'info' | 'success' | 'warning' | 'error' = 'info', timeout: number = 3000) => {
    if (timeout === 0) timeout = -1 // -1 means the snackbar will not close automatically
    alertSnackbarState.value = { visible: true, color, message, timeout }
  }

  return {
    theme,
    applicationName,
    applicationOfficialName,
    applicationDomain,
    userTimezone,
    railCollapsedState,
    accountId,
    userId,
    accountProfile,
    accountNamespace,
    nativeEngine,
    nativeAppName,
    userProfile,
    snowflakeSession,
    setSnowflakeSession,
    availableSnowflakeRoles,
    setAvailableSnowflakeRoles,
    activeSnowflakeRole,
    setActiveSnowflakeRole,
    alertSnackbarState,
    alertSnackbar,
    toggleTheme,
    syncAccountProfile,
    syncUserProfile,
  }
})
